/**
 * Panel registry for the dockable map workspace (components/map/DockLayout.js).
 *
 * Every panel the map workspace can show is declared once here: id, title, icon,
 * default dock, default size and whether it is a singleton. The open/closed state of
 * each panel still lives in hooks/useMapPanels (plus the nearest tool and the task
 * console / raster hosts); `isOpen` / `setOpen` below are the thin compatibility
 * layer that maps those existing booleans onto registry ids, so no existing hook
 * consumer changes. Pure module: no React, no DOM.
 *
 * Adding a panel: add an entry here, add its element to the content map in
 * components/map/MapSidePanels.js / MapBottomPanels.js (or MapView for floating-only
 * panels) and, if it has an open flag, its `isOpen` / `setOpen` pair. Layout state
 * (lib/dock-layout.js) picks it up on the next load.
 *
 * `ctx` is the object MapView builds: everything useMapPanels returns plus
 * { rawDatasetId, rasterOpen, taskConsole: { open, setOpen }, closeDrawingPanel,
 *   setDrawMode, setChartFilter }.
 */

export const DOCKS = ['left', 'right', 'bottom'];
/** Pseudo-dock for panels shown as floating windows. */
export const FLOAT = 'float';

const flag = (key, setter) => ({
  isOpen: (ctx) => !!ctx[key],
  setOpen: (ctx, open) => ctx[setter]?.(!!open),
});

/**
 * defaultSize: { pct } is the dock size (percent of the workspace) when this panel is
 * what fills the dock; { w, h } is the floating window size in pixels.
 */
export const PANELS = [
  { id: 'layers', title: 'Layers', icon: 'Layers', defaultDock: 'left', defaultSize: { pct: 18, w: 260, h: 420 }, singleton: true, floatable: false,
    ...flag('showLayerPanel', 'setShowLayerPanel') },
  { id: 'workflow', title: 'Workflow', icon: 'ListChecks', defaultDock: 'left', defaultSize: { pct: 18, w: 260, h: 480 }, singleton: true, floatable: false,
    ...flag('showWorkflowPanel', 'setShowWorkflowPanel') },
  { id: 'agents', title: 'Field Agents', icon: 'Bot', defaultDock: 'right', defaultSize: { pct: 22, w: 280, h: 420 }, singleton: true, floatable: false,
    ...flag('showAgentPanel', 'setShowAgentPanel') },
  { id: 'properties', title: 'Properties', icon: 'Settings2', defaultDock: 'right', defaultSize: { pct: 22, w: 280, h: 480 }, singleton: true, floatable: false,
    ...flag('showStyleEditor', 'setShowStyleEditor') },
  { id: 'analysis', title: 'Analysis', icon: 'Activity', defaultDock: 'right', defaultSize: { pct: 24, w: 300, h: 520 }, singleton: true, floatable: false,
    ...flag('showAnalysisPanel', 'setShowAnalysisPanel') },
  { id: 'inversion', title: 'Inversion results', icon: 'Waypoints', defaultDock: 'right', defaultSize: { pct: 22, w: 290, h: 520 }, singleton: true, floatable: false,
    ...flag('showInversionPanel', 'setShowInversionPanel') },
  { id: 'viewer3d', title: '3D view', icon: 'Box', defaultDock: 'right', defaultSize: { pct: 30, w: 420, h: 520 }, singleton: true, floatable: true,
    ...flag('showViewer3dPanel', 'setShowViewer3dPanel') },
  { id: 'gridCombine', title: 'Grid math', icon: 'Combine', defaultDock: 'right', defaultSize: { pct: 22, w: 280, h: 480 }, singleton: true, floatable: false,
    ...flag('showGridCombine', 'setShowGridCombine') },
  { id: 'drawing', title: 'Drawing', icon: 'Pencil', defaultDock: 'right', defaultSize: { pct: 20, w: 240, h: 420 }, singleton: true, floatable: false,
    isOpen: (ctx) => !!ctx.showDrawingPanel,
    setOpen: (ctx, open) => {
      if (open) ctx.setShowDrawingPanel?.(true);
      else if (ctx.closeDrawingPanel) ctx.closeDrawingPanel();
      else ctx.setShowDrawingPanel?.(false);
    } },
  { id: 'attributes', title: 'Attributes', icon: 'Table2', defaultDock: 'bottom', defaultSize: { pct: 32, w: 640, h: 320 }, singleton: true, floatable: false,
    ...flag('showAttributeTable', 'setShowAttributeTable') },
  { id: 'section', title: 'Section', icon: 'TrendingUp', defaultDock: 'bottom', defaultSize: { pct: 36, w: 760, h: 380 }, singleton: true, floatable: false,
    ...flag('showSectionView', 'setShowSectionView') },
  { id: 'pseudosection', title: 'Pseudosection', icon: 'Grid3x3', defaultDock: 'bottom', defaultSize: { pct: 36, w: 760, h: 380 }, singleton: true, floatable: false,
    isOpen: (ctx) => !!ctx.pseudosection,
    setOpen: (ctx, open) => ctx.setPseudosection?.(open ? { datasetId: ctx.rawDatasetId || null } : null) },
  { id: 'variogram', title: 'Variogram', icon: 'Target', defaultDock: 'bottom', defaultSize: { pct: 34, w: 700, h: 360 }, singleton: true, floatable: false,
    ...flag('showVariogram', 'setShowVariogram') },
  { id: 'charts', title: 'Charts', icon: 'BarChart3', defaultDock: 'bottom', defaultSize: { pct: 34, w: 700, h: 360 }, singleton: true, floatable: false,
    isOpen: (ctx) => !!ctx.showChartPanel,
    setOpen: (ctx, open) => { ctx.setShowChartPanel?.(!!open); if (!open) ctx.setChartFilter?.(null); } },
  { id: 'gridProfile', title: 'Grid profile', icon: 'Ruler', defaultDock: 'bottom', defaultSize: { pct: 34, w: 700, h: 360 }, singleton: true, floatable: false,
    isOpen: (ctx) => !!ctx.showGridProfile,
    setOpen: (ctx, open) => { ctx.setShowGridProfile?.(!!open); if (!open) ctx.setDrawMode?.(null); } },
  { id: 'taskConsole', title: 'Tasks', icon: 'ClipboardList', defaultDock: 'bottom', defaultSize: { pct: 26, w: 520, h: 240 }, singleton: true, floatable: true,
    isOpen: (ctx) => !!ctx.taskConsole?.open,
    setOpen: (ctx, open) => ctx.taskConsole?.setOpen?.(!!open) },
  { id: 'raster', title: 'Raster layers', icon: 'Image', defaultDock: 'float', defaultSize: { pct: 22, w: 290, h: 360 }, singleton: true, floatable: true,
    isOpen: (ctx) => !!ctx.rasterOpen,
    setOpen: () => { /* data driven: closing only dismisses the window (DockLayout) */ } },
  { id: 'nearest', title: 'Find nearest', icon: 'Crosshair', defaultDock: 'right', defaultSize: { pct: 22, w: 290, h: 340 }, singleton: true, floatable: true,
    isOpen: (ctx) => ctx.tool === 'nearest',
    setOpen: (ctx, open) => ctx.setTool?.(open ? 'nearest' : 'select') },
];

const BY_ID = new Map(PANELS.map((p) => [p.id, p]));

export function getPanel(id) { return BY_ID.get(id) || null; }
export function isKnownPanel(id) { return BY_ID.has(id); }
export function panelIds() { return PANELS.map((p) => p.id); }

/** Ids of the panels that are currently open, in registry order. */
export function openPanelIds(ctx) {
  return PANELS.filter((p) => { try { return p.isOpen(ctx); } catch { return false; } }).map((p) => p.id);
}

/** Open or close one panel through the existing hook setters. Unknown ids are ignored. */
export function setPanelOpen(ctx, id, open) {
  const p = BY_ID.get(id);
  if (p) p.setOpen(ctx, open);
}

/** Default floating rectangle for a panel; cascades by `index` so windows do not stack exactly. */
export function defaultFloatRect(id, index = 0) {
  const p = BY_ID.get(id);
  const { w = 280, h = 320 } = p?.defaultSize || {};
  return { x: 16 + index * 28, y: 16 + index * 28, w, h };
}
